import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';

export interface LanguagesSpecialisationsSectionProps {
  requiredLanguages?: string[];
  optionalLanguages?: string[];
  specialisations?: string[];
}

export const LanguagesSpecialisationsSection: React.FC<LanguagesSpecialisationsSectionProps> = ({
  requiredLanguages,
  optionalLanguages,
  specialisations,
}) => {
  const intl = useIntl();

  if (!requiredLanguages?.length && !optionalLanguages?.length && !specialisations?.length) {
    return null;
  }

  // Combine required and optional languages for display
  const allLanguages = [
    ...(requiredLanguages || []).map(lang => ({ lang, isRequired: true })),
    ...(optionalLanguages || []).map(lang => ({ lang, isRequired: false })),
  ];

  return (
    <div className="mb-6 p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-800/40 border border-gray-200/60 dark:border-gray-700/60 shadow-sm">
      {/* Languages (Required + Optional) */}
      {allLanguages.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
            <FormattedMessage 
              id="jobDetailPage.requiredLanguages" 
              defaultMessage="Required Languages" 
              description="Section heading for list of required languages for the job" 
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {allLanguages.map(({ lang, isRequired }, idx) => (
              <span 
                key={lang + idx} 
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
                  isRequired
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700'
                    : 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600'
                }`}
              >
                {lang}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Specialisations */}
      {specialisations && specialisations.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
            <FormattedMessage 
              id="jobDetailPage.specialisations" 
              defaultMessage="Specialisations" 
              description="Section heading for job specializations or focus areas" 
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {specialisations.map((spec, idx) => (
              <span 
                key={spec + idx} 
                className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700/60 text-gray-700 dark:text-gray-300 text-sm font-medium border border-gray-200 dark:border-gray-600"
              >
                {fixMojibake(spec)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
